import qs from 'qs';
import { PermissionCode, SurveyJobSearchField } from 'types';

/**
 * Userflow injects its product-tour UI at a base z-index of 1234500, far above the
 * antd popup layer (1000-2000), so it paints over modals.
 */
export const ABOVE_USERFLOW_ICON_Z_INDEX = 1234600;

export const HEADER_HEIGHT = 56;

export const PATH = {
  DEFAULT: '/',
  BLUEPRINTS: '/blueprints',
  LOGIN: '/login',
  AUTH_CALLBACK: '/auth/callback',
  AUTH_VERIFY_SSO: '/auth/verify-sso',
  AUTH_DENIED: '/auth/denied',
  LOGOUT: '/logout',
  HEALTH: '/health',
  SETTINGS_SSO_CONFIGURATION: '/settings/ssoConfiguration',
  SECURITY_EVENT_API: '/settings/securityEventAPI',
  PERCENTILES: '/percentiles',
  CUSTOM_PERCENTILES: '/percentiles/custom',
  SETTINGS_HRIS: '/settings/hris',
  SETTINGS_HRIS_SFTP: '/settings/hris/sftp',
  SETTINGS_HRIS_WORKDAY_RAAS: '/settings/hris/workdayRaas',
  SETTINGS_JOB_MAPPINGS_MODE: '/settings/jobMappingMode',
  SETTINGS_JDXPERT: '/settings/jdxpert',
  SETTINGS_AI_FEATURES: '/settings/aiFeatures',
  SETTINGS_ASK_BETTERCOMP_PROMPTS: '/settings/askBettercompPrompts',
  DATA_RESET: '/settings/reset',
  TABLE_PRICEJOB: ({ compositeId }: { compositeId?: number } = {}) => `/market/pricejob${compositeId ? `?${qs.stringify({ compositeId })}` : ''}`,
  TABLE_PRICEJOB_MASS: '/market/pricejobMass',
  TABLE_PRICEJOB_MASS_POLICY_HISTORY: '/market/pricejobMass?openPolicyHistory=true',
  TABLE_PAYMARKET: '/configure/paymarket',
  TABLE_TAGS: '/configure/tags',
  TABLE_SURVEYS: '/configure/surveys',
  FIELD_MAPPING_CONFIGURATIONS: '/surveys/mappingConfigurations',
  FIELD_MAPPING_CONFIGURATION_EDITOR: (id: string) => `/surveys/mappingConfigurations/editor?${qs.stringify({ id })}`,
  // A brand-new (still-unsaved) configuration: no id yet — the editor opens in draft mode seeded
  // from the vendor identity, and the profile is created only at the final save.
  FIELD_MAPPING_CONFIGURATION_EDITOR_NEW: (
    { surveyVendorId, customVendorName, pickTemplate }: { surveyVendorId?: string; customVendorName?: string; pickTemplate?: boolean },
  ) => `/surveys/mappingConfigurations/editor?${qs.stringify({ new: 1, surveyVendorId, customVendorName, pickTemplate: pickTemplate ? 1 : undefined })}`,
  FIELD_MAPPING_CONFIGURATION_VIEWER: (id: string) => `/surveys/mappingConfigurations/view?${qs.stringify({ id })}`,
  SURVEY_JOBS_MANAGEMENT: ({ id }: { id: string }) => `/survey/${id}/jobs`,
  // The prior survey lives in the `surveyId` query param, not a path segment: the app is
  // statically exported, so a dynamic path has no object behind it and a hard reload is
  // served the SPA shell (the dashboard) instead of the wizard. Same reasoning as
  // /aiAssistant?id= and SURVEY_PROFILE_MAPPING below.
  SURVEY_SETUP_WIZARD: ({ id, planId, seasonId, seasonRowId }: { id: string; planId: number; seasonId: number; seasonRowId: number }) => `/survey/setup?${qs.stringify({ surveyId: id, planId, seasonId, seasonRowId })}`,
  SURVEY_PROFILE_MAPPING: (
    { replacementId, planId, surveyJobProfileId }:
    { replacementId: number; planId: number; surveyJobProfileId?: string },
  ) => `/survey/profile-mapping?${qs.stringify({ replacementId, planId, surveyJobProfileId })}`,
  TABLE_PARTICIPATION: '/configure/participation',
  TABLE_POLICIES: '/policies',
  RANGES: '/ranges',
  RANGE_ANALYSIS: '/ranges/rangeAnalysis',
  RANGE_ELIGIBILITY: ({ planId }: { planId?: number }) => `/ranges/eligibility?${qs.stringify({ planId })}`,
  RANGE_GUIDELINES: ({ planId }: { planId?: number }) => `/ranges/guidelines?${qs.stringify({ planId })}`,
  RANGE_SETS: ({ planId }: { planId?: number }) => `/ranges/sets?${qs.stringify({ planId })}`,
  RANGE_GUIDELINE_VIEW: ({ id, planId }: { id: number, planId: number }) => `/ranges/guidelines/view?${qs.stringify({ id, planId })}`,
  RANGE_SPREAD: '/ranges/rangeSpread',
  RANGE_ASSIGNMENT: '/ranges/assignment',
  TABLE_USERS: ({ dataAccessGroupId }: { dataAccessGroupId?: number } = {}) => dataAccessGroupId ? `/configure/users?${qs.stringify({ dataAccessGroupId })}` : '/configure/users',
  TABLE_ROLES: '/configure/roles',
  DATA_ACCESS_GROUPS: '/configure/dataAccessGroup',
  ASSIGN_POLICIES: '/policies/assign',
  ROLE_PERMISSIONS: (roleId: number) => `/configure/roles/permissions?${qs.stringify({ roleId })}`,
  FILES_UPLOAD: '/configure/files',
  TABLE_PLANS: '/plans',
  TABLE_CURRENCY: '/currency',
  TABLE_JOB_MAPPING: '/mapping/job',
  JOB_MAPPING_RECOMMENDATIONS: ({
    planId,
    surveyJobProfileId,
    companyJobFamilyId,
    companyJobSubFamilyId,
    surveyJobFocusIds,
    surveyJobFamilyIds,
    surveyJobSubFamilyIds,
  }: JobMappingRecommendationsParams) => `/mapping/job/recommendations?${qs.stringify({
      planId,
      surveyJobProfileId,
      companyJobFamilyId,
      companyJobSubFamilyId,
      surveyJobFocusIds,
      surveyJobFamilyIds,
      surveyJobSubFamilyIds,
    }, { arrayFormat: 'brackets' })}`,
  TABLE_LEVEL_MAPPING: '/mapping/level',
  VISUAL_LEVEL_MAPPING: '/mapping/level/visual',
  MULTI_LEVEL_MAPPING: '/mapping/level/multi',
  LEVELS: '/levels',
  CATEGORIES: '/categories',
  TABLE_PAYMARKET_MAPPING: '/mapping/paymarket',
  TABLE_EMPLOYEES: '/employees',
  CONFIGURE_AGING_RULES: '/configure/agingRules',
  UPLOAD_EMPLOYEES: '/upload/employees',
  UPLOAD_SURVEYS: '/upload/surveys',
  UPLOAD_RANGES: '/upload/ranges',
  UPLOAD_JOBS: '/upload/jobs',
  COMPARE_CUTS: '/compare/cuts',
  REPORT: '/report',
  COMPANY_JOBS: '/jobs',
  COMPANY_JOBS_REWRITES: '/ranges/rewrites',
  COMPANY_JOBS_FAMILIES: '/jobs/families',
  COMPANY_JOBS_SUB_FAMILIES: '/jobs/subFamilies',
  EMPLOYEES_FILES: (id: number) => `/employees/files?${qs.stringify({ id })}`,
  REPORT_VIEW: (id: number, isGlobal?: boolean) => `/report/view?${qs.stringify({ id, isGlobal })}`,
  MODEL: '/model',
  MODEL_SALARY_RANGE: ({ id, isRecalculateStats }: { id: number, isRecalculateStats?: boolean }) => `/model/salaryRange?${qs.stringify({ id, isRecalculateStats })}`,
  MODEL_SALARY_RANGE_SETS: ({ id }: { id: number }) => `/model/salaryRange/rangeSet?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_JOBS: ({ id }: { id: number }) => `/model/salaryRange/rangeJobs?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_ANALYSIS: ({ id }: { id: number }) => `/model/salaryRange/rangeAnalysis?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_SETTINGS_ELIGIBILITY: ({ id }: { id: number }) => `/model/salaryRange/eligibility?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_RANGE_GUIDELINES: ({ id }: { id: number }) => `/model/salaryRange/guidelines?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_SETTINGS: ({ id }: { id: number }) => `/model/salaryRange/settings?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_RANGE_MINIMUMS: ({ id }: { id: number }) => `/model/salaryRange/minimums?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_RANGE_SPREAD: ({ id }: { id: number }) => `/model/salaryRange/rangeSpread?${qs.stringify({ id })}`,
  MODEL_SALARY_RANGE_RANGE_ASSIGNMENT: ({ id }: { id: number }) => `/model/salaryRange/rangeAssignment?${qs.stringify({ id })}`,
  DOWNLOADS: '/downloads',
  PARTICIPATION: ({ id }: { id: string }) => `/participation?${qs.stringify({ id })}`,
  PARTICIPATION_FIELDS_MAPPING: ({ id }: { id: string }) => `/participation/${id}/fieldsMapping`,
  PARTICIPATION_JOB_MAPPING: ({ id }: { id: string }) => `/participation/${id}/jobMapping`,
  QUICKSIGHT_DASHBOARD: (dashboardId: string, reportId?: string) => `/analyze/dashboards/view?${qs.stringify({ dashboardId, reportId })}`,
  QUICKSIGHT: '/analyze/dashboards',
  QUICKSIGHT_AUTHORING: '/analyze/authoring',
  DATA_LOADERS: '/configure/dataLoaders',
  DATA_MANAGEMENT: '/dataManagement',
  AI_ASSISTANT: '/aiAssistant',
  SURVEY_RESULTS_SEASON: '/surveyResultsSeason',
};

export type JobMappingRecommendationsParams = {
  planId: number;
  surveyJobProfileId: string;
  companyJobFamilyId?: number;
  companyJobSubFamilyId?: number;
  surveyJobFocusIds?: string[];
  surveyJobFamilyIds?: string[];
  surveyJobSubFamilyIds?: string[];
};

export const REQUEST_TYPE = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
};

export const CELL_FORMATS = {
  number: 'number',
  currency: 'currency',
  percentage: 'percentage',
  date: 'date',
} as const;

type CurrencyPayloadProps = {
  fromCurrencyCode?: string,
  toCurrencyCode?: string,
  fromRate?: number,
  toRate?: number,
};

export type CurrencyPayload = {
  currency?: string;
  rate: number
}

export const DEFAULT_CURRENCY = 'USD';

export function getCurrencyPayload({
  fromCurrencyCode,
  toCurrencyCode,
  fromRate = 1,
  toRate = 1,
}: CurrencyPayloadProps): CurrencyPayload {
  return {
    currency: toCurrencyCode || fromCurrencyCode,
    rate: (toCurrencyCode && fromCurrencyCode && toCurrencyCode !== fromCurrencyCode)
      ? toRate/fromRate
      : 1,
  };
}

export const SurveyJobSearchFilterOptions = [
  {
    label: 'Survey Job Title',
    value: SurveyJobSearchField.JOB_TITLE,
  },
  {
    label: 'Survey Job Code',
    value: SurveyJobSearchField.JOB_CODE,
  },
  {
    label: 'Survey Job Description',
    value: SurveyJobSearchField.JOB_DESCRIPTION,
  },
]

export const PermissionTitle: { [K in PermissionCode]: string } = {
  [PermissionCode.VIEW_USERS]: 'View Users',
  [PermissionCode.MANAGE_USERS]: 'Manage Users',
  [PermissionCode.MANAGE_ROLES]: 'Manage Roles',
  [PermissionCode.MANAGE_PLANS]: 'Manage Plans',
  [PermissionCode.MANAGE_EMPLOYEES]: 'Manage Employees',
  [PermissionCode.VIEW_COMPOSITE_EMPLOYEES]: 'View Composite Employees',
  [PermissionCode.VIEW_SURVEY_PARTICIPATIONS]: 'View Survey Participation',
  [PermissionCode.MANAGE_PAYMARKETS]: 'Manage Paymarkets',
  [PermissionCode.MANAGE_CURRENCIES]: 'Manage Currencies',
  [PermissionCode.MANAGE_AGING]: 'Manage Aging Rules',
  [PermissionCode.MANAGE_TAGS]: 'Manage Tags',
  [PermissionCode.MANAGE_SURVEYS]: 'Manage Surveys',
  [PermissionCode.LOAD_FILES]: 'Load Files',
  [PermissionCode.MANAGE_LEVEL_MAPPINGS]: 'Manage Level Mappings',
  [PermissionCode.MANAGE_DATA_CUT_PAYMARKET_MAPPINGS]: 'Manage DataCut Paymarket Mappings',
  [PermissionCode.MANAGE_COMPANY_JOBS]: 'Manage Company Jobs',
  [PermissionCode.MANAGE_API_SETTINGS]: 'Manage API Settings',
  [PermissionCode.MANAGE_PRICING_POLICIES]: 'Manage Pricing Policies',
  [PermissionCode.MANAGE_TARGET_PERCENTILES]: 'Manage Target Percentiles',
  [PermissionCode.MANAGE_CUSTOM_PERCENTILES]: 'Manage Custom Percentiles',
  [PermissionCode.MANAGE_DATA_MODELS]: 'Full access',
  [PermissionCode.VIEW_ANALYZE_DASHBOARDS]: 'Analyze Dashboards',
  [PermissionCode.MANAGE_REPORTS]: 'Manage Reports',
  [PermissionCode.MARKET_PRICING]: 'Full access',
  [PermissionCode.MANAGE_JOB_MAPPING_MODE]: 'Manage Job Mappings Mode',
  [PermissionCode.MANAGE_DATA_ACCESS_GROUPS]: 'Manage Data Access Groups',
  [PermissionCode.MANAGE_DATA_LOADER]: 'Manage Data Loader',
  [PermissionCode.UPLOAD_DATA_FILES]: 'Upload Data Files',
  [PermissionCode.VIEW_DATA_HISTORY]: 'View Data History',
  [PermissionCode.CANCEL_DATA_LOADS]: 'Cancel Data Loads',
  [PermissionCode.EDIT_DATA_TRANSFORMATIONS]: 'Edit Data Transformations',
  [PermissionCode.DOWNLOAD_DATA_ERRORS]: 'Download Data Errors',
  [PermissionCode.BYPASS_SCHEMA_VALIDATION]: 'Bypass Schema Validation',
  [PermissionCode.REQUEST_DATA_SUPPORT]: 'Request Data Support',
  [PermissionCode.MANAGE_IDP]: 'Manage Idp',
  [PermissionCode.REPLACE_CUT_MAPPINGS]: 'Replace Cut Mappings',
  [PermissionCode.MANAGE_SECURITY_EVENT_API]: 'Manage Security Event API',
  [PermissionCode.REPLACE_UPDATE_SURVEY]: 'Replace/Update Survey',
  [PermissionCode.MANAGE_RANGE_ASSIGNMENTS]: 'Create/Manage Range Assignments',
  [PermissionCode.MANAGE_TENANCY_SWITCHER]: 'Manage Tenancy Switcher',
  [PermissionCode.MANAGE_AI_FEATURES]: 'Manage AI Features',
  [PermissionCode.MASS_APPLY_PRICING_POLICIES]: 'Policy Manager',
  [PermissionCode.MASS_ENHANCED_APPLY_PRICING_POLICIES]: 'Mass Enhanced Apply Pricing Policies',
  [PermissionCode.ADD_JOBS_TO_PRIVATE_SURVEYS]: 'Add jobs to private surveys',
  [PermissionCode.VIEW_SURVEY_SEASON_WORKFLOW]: 'View Survey Refresh Workflow',
  [PermissionCode.VIEW_MARKET_PRICING]: 'Basic View',
  [PermissionCode.COMMENT_MARKET_PRICING]: 'Commenting',
  [PermissionCode.VIEW_DATA_MODELS]: 'Basic View',
}

export const VERIFY_SSO_MESSAGE_SOURCE = 'bc-test-sso-configuration';

export const anonymousRoutes = [
  PATH.AUTH_CALLBACK,
  PATH.AUTH_VERIFY_SSO,
  PATH.AUTH_DENIED,
  PATH.LOGOUT,
  PATH.HEALTH,
];

export const MAPPING_SURVEY_JOB_PROFILE_SETTINGS_KEY = 'activeMappingSurveyJobProfile';
export const MAPPING_VENDOR_LEVEL_PROFILE_SETTINGS_KEY = 'activeVendorLevelProfile';

export const GLOBAL_LEVEL_NOT_APPLICABLE = 'notApplicable'

export const SURVEY_JOB_DESCRIPTION_COLUMN_TITLE = 'surveyJobDescription';
